import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { dealApi, dealQueryKeys } from '@crm/entities/deal';
import { toastManager } from '@crm/entities/toast';
import { getApiErrorMessage } from '@crm/shared/api';

import type { TDeal, TDealDetails, TGetDealTasksParams } from '@crm/entities/deal';

type TDealDetailsWithTaskListIds = Omit<TDealDetails, 'tasksData'> & {
    tasksData: Omit<TDealDetails['tasksData'], 'tabs'> & {
        tabs: Omit<TDealDetails['tasksData']['tabs'], 'list'> & {
            list: string[];
        };
    };
};

const getTaskIds = (list: unknown) => {
    if (Array.isArray(list)) {
        return list.filter((taskId): taskId is string => typeof taskId === 'string');
    }

    if (list && typeof list === 'object') {
        return Object.values(list).flatMap((taskIds) =>
            Array.isArray(taskIds)
                ? taskIds.filter((taskId): taskId is string => typeof taskId === 'string')
                : []
        );
    }

    return [];
};

export const useGetDealTasksMutation = ({ dealId }: { dealId: TDeal['id'] }) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: [...dealQueryKeys.detail(dealId), 'tasks', 'get'],
        mutationFn: ({ payload }: Omit<TGetDealTasksParams, 'dealId'>) =>
            dealApi.getDealTasks({ dealId, payload }),
        onSuccess: (tasksData) => {
            queryClient.setQueryData<TDealDetailsWithTaskListIds>(
                dealQueryKeys.detail(dealId),
                (dealDetails) => {
                    if (!dealDetails) {
                        return dealDetails;
                    }

                    return {
                        ...dealDetails,
                        tasksData: {
                            ...tasksData,
                            tabs: {
                                ...tasksData.tabs,
                                list: getTaskIds(tasksData.tabs.list),
                            },
                        },
                    };
                }
            );
        },
        onError: (error) => {
            toastManager.onError({
                title: getApiErrorMessage(
                    error,
                    t('crm.dealDetailsContent.tasksBlock.errors.changeSort')
                ),
            });
        },
    });
};
